// @ts-nocheck - TODO: properly type this file!

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Divider, Empty, Flex, List, Space, Tooltip } from 'antd';
import {
  DndContext,
  PointerSensor,
  KeyboardSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  arrayMove,
  sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';

import headAvatar from '@/assets/images/HeadAvatar.svg';
import imgAvatar from '@/assets/images/ImgAvatar.svg';
import emailAvatar from '@/assets/images/MsgAvatar.svg';
import { CustomTitle } from '@/components';
import { DeleteIcon, NoteAdd, PencilIcon } from '@/assets/icons/icons';
import IconButton from './IconButton';
import SortableRow from './SortableRow';
import { ellipsisInline } from './styles';
import styles from './DirectContactManage.module.css';
import type { Contact, CourierProviderData, ApiContact } from './types';
import Flag from './Flag';
import { mapApiContactToContact } from './utils';

type Props = {
  providerData: CourierProviderData;
  title?: string;
  externalItems?: Contact[];
  onChange?: (contacts: Contact[]) => void;
  onEdit?: (apiContact) => void;
  onAddNote?: (contact: Contact) => void;
};

const DirectContactManage: React.FC<Props> = ({
  onChange,
  onEdit,
  onAddNote,
  externalItems,
  title = 'When using AI to resolve, these will be contacted in order (top → bottom)',
}) => {
  const [items, setItems] = useState<Contact[]>([]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const extSig = useMemo(() => {
    if (!externalItems) return 'nil';
    return externalItems
      .map((c) => {
        const a = c.api ?? c;
        return [
          a.id,
          a.contact_type ?? '',
          (a as ApiContact).label ?? '',
          a.name ?? '',
          (a as ApiContact).title ?? '',
          a.email ?? '',
          (a as ApiContact).phone_number ?? '',
          (a as ApiContact).operating_hours ?? '',
          (a as ApiContact).notes ?? (a as ApiContact).usage_notes ?? '',
          (a as ApiContact).is_primary_contact ?? '',
          (a as ApiContact).order_index ?? '',
          (a as ApiContact).updated_at ?? '',
        ].join('|');
      })
      .join('||');
  }, [externalItems]);

  const getDeleteState = useCallback(
    (item: Contact) => {
      const total = items?.length ?? 0;
      const isPrimary =
        Boolean(item.api?.is_primary_contact) || Boolean(item?.is_primary);

      if (isPrimary && total <= 1) {
        return {
          allowed: false as const,
          reason: 'Must have at least one active contact',
        };
      }

      return { allowed: true as const, reason: '' };
    },
    [items]
  );

  // const setPrimaryFlags = (c: Contact, isPrimary: boolean): Contact => ({
  //   ...c,
  //   is_primary: isPrimary,
  //   is_primary_contact: isPrimary,
  //   api: c.api ? { ...c.api, is_primary_contact: isPrimary } : c.api,
  // });

  const handleReorder = useCallback(
    (oldIndex: number, newIndex: number) => {
      setItems((prev) => {
        const moved = arrayMove(prev ?? [], oldIndex, newIndex);

        const next = moved.map((c, i) => {
          const isPrimary = String(c.id) === String(moved[0]?.id);

          return {
            ...c,
            is_primary: isPrimary,
            is_primary_contact: isPrimary,
            api: c.api
              ? {
                  ...c.api,
                  order_index: i,
                  is_primary_contact: isPrimary,
                }
              : c.api,
          };
        });

        onChange?.(next);
        return next;
      });
    },
    [onChange]
  );

  const handleDelete = useCallback(
    (id: string | number) => {
      setItems((prev) => {
        const next = (prev ?? []).filter((x) => x.id !== id);
        onChange?.(next);
        return next;
      });
    },
    [onChange]
  );

  useEffect(() => {
    if (externalItems === undefined) {
      setItems([]);
      return;
    }
    const normalized: Contact[] = (externalItems || []).map((c) => {
      const mapped = mapApiContactToContact(c.api as ApiContact);
      return {
        ...mapped,
        isActive: c?.isActive ?? mapped.isActive ?? false,
      };
    });
    setItems((prev) => {
      const sameLength = prev.length === normalized.length;
      const same =
        sameLength &&
        prev.every((p, i) => {
          const n = normalized[i];
          return (
            p.id === n.id &&
            p.name === n.name &&
            p.email === n.email &&
            p.phone === n.phone &&
            p.hours === n.hours &&
            p.note === n.note &&
            p.contact_type === n.contact_type &&
            (p.api?.order_index ?? null) === (n.api?.order_index ?? null) &&
            (p.api?.updated_at ?? null) === (n.api?.updated_at ?? null) &&
            p.isActive === n.isActive
          );
        });
      return same ? prev : normalized;
    });
  }, [externalItems, extSig]);

  const handleEdit = useCallback(
    (apiContact) => onEdit?.(apiContact),
    [onEdit]
  );

  const handleNoteAdd = useCallback(
    (contact: Contact) => onAddNote?.(contact),
    [onAddNote]
  );

  const list = useMemo(
    () => (
      <List
        rowKey={(item) => String((item as Contact).id)}
        itemLayout='vertical'
        dataSource={items}
        style={{ overflowX: 'hidden' }}
        renderItem={(item: Contact) => {
          const baseText =
            item.contact_type === 'general_support'
              ? (item.api?.label ?? item.name)
              : (item.api?.name ?? item.name);

          const primaryText = item.is_primary
            ? ` ${baseText} (Primary)`
            : baseText;

          const { allowed, reason } = getDeleteState(item);
          const isProtected = (it?: typeof item) =>
            !!it &&
            (it.default === true ||
              it.editable === false ||
              (it.contact_type === 'general_support' &&
                it.api.topic.toLowerCase() !== 'general'));
          const canEdit = !!item && !isProtected(item);
          const canNote =
            !!item &&
            item?.contact_type === 'general_support' &&
            !isProtected(item);
          const canDelete =
            !!item &&
            (item.contact_type === 'general_support' ||
              item.contact_type === 'direct') &&
            // item.default !== true &&
            item.editable !== false;

          return (
            <SortableRow id={String(item.id)} showDrag>
              <img
                src={
                  item?.contact_type === 'direct'
                    ? imgAvatar
                    : item?.contact_type === 'general_support' && item.email
                      ? emailAvatar
                      : item?.contact_type === 'general_support' && item.phone
                        ? headAvatar
                        : headAvatar
                }
                alt=''
              />

              <div className={styles.details}>
                <Flex gap={8} align='center' className='min-w-0'>
                  <CustomTitle
                    text={primaryText}
                    fontSize='12px'
                    lineHeight='16px'
                    fontWeight={600}
                    marginBottom={'0px'}
                    color='black'
                    style={ellipsisInline}
                  />
                </Flex>
                {item.contact_type === 'direct' &&
                  (item.api as ApiContact)?.title && (
                    <CustomTitle
                      marginTop='0px'
                      marginBottom='0px'
                      text={(item.api as ApiContact).title!}
                      fontSize='12px'
                      lineHeight='16px'
                      fontWeight={400}
                      style={ellipsisInline}
                    />
                  )}

                <Flex vertical gap={2} className='min-w-0 word-break'>
                  {item.email && (
                    <CustomTitle
                      marginTop='0px'
                      marginBottom='0px'
                      text={item.email}
                      fontSize='12px'
                      lineHeight='16px'
                      fontWeight={400}
                      style={{ ...ellipsisInline, textDecoration: 'underline' }}
                    />
                  )}

                  {item.phone && (
                    <div>
                      <CustomTitle
                        text={
                          <span title={item.phone} className={styles.phone}>
                            <Flag country={item.country} /> {item.phone}
                          </span>
                        }
                        color={'black'}
                        marginBottom={'0'}
                        fontSize={'12px'}
                        lineHeight={'16px'}
                        fontWeight={400}
                        marginTop={'0px'}
                        style={{ ...ellipsisInline }}
                      />
                    </div>
                  )}
                </Flex>

                {item.hours && !item.default && (
                  <CustomTitle
                    marginTop='0px'
                    marginBottom={'0px'}
                    text={item.hours}
                    fontSize='12px'
                    lineHeight='16px'
                    fontWeight={400}
                    style={ellipsisInline}
                  />
                )}

                {item.note && (
                  <div>
                    <span
                      className={
                        item.default
                          ? `${styles.note} ${styles.noteDefault}`
                          : styles.note
                      }
                    >
                      {item.note}
                    </span>
                  </div>
                )}
              </div>

              <Space style={{ flexShrink: 0, whiteSpace: 'nowrap' }}>
                {canEdit && (
                  <IconButton
                    ariaLabel='Edit contact'
                    onClick={() => handleEdit(item.api)}
                  >
                    <PencilIcon color='var(--text-primary)' />
                  </IconButton>
                )}

                {canNote && (
                  <IconButton
                    ariaLabel='Add note'
                    onClick={() => handleNoteAdd(item)}
                  >
                    <NoteAdd />
                  </IconButton>
                )}
                <Tooltip
                  title={allowed ? '' : reason}
                  open={allowed ? false : undefined}
                >
                  <span>
                    {canDelete && (
                      <IconButton
                        ariaLabel={allowed ? 'Delete contact' : reason}
                        onClick={
                          allowed
                            ? () => handleDelete(item.id as string)
                            : undefined
                        }
                        color={allowed ? 'var(--text-primary)' : undefined}
                        aria-disabled={!allowed}
                      >
                        <DeleteIcon
                          color={
                            allowed ? 'var(--text-primary)' : 'var(--gray-600)'
                          }
                        />
                      </IconButton>
                    )}
                  </span>
                </Tooltip>
              </Space>
            </SortableRow>
          );
        }}
      />
    ),
    [items, getDeleteState, handleEdit, handleNoteAdd, handleDelete]
  );

  return (
    <div className={styles.root}>
      <CustomTitle
        text={title}
        fontSize='12px'
        lineHeight='20px'
        color='var(--text-secondary)'
        style={{ marginLeft: 8, ...ellipsisInline }}
        fontWeight='400'
      />
      <Divider style={{ margin: '8px 0 12px' }} />
      <div className={styles.listScroll}>
        {(items?.length ?? 0) === 0 ? (
          <div className={styles.empty}>
            <Empty description='No contacts' />
          </div>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={({ active, over }) => {
              if (!over || active.id === over.id) return;
              const ids = (items ?? []).map((i) => String(i.id));
              const from = ids.indexOf(String(active.id));
              const to = ids.indexOf(String(over.id));
              if (from !== -1 && to !== -1) handleReorder(from, to);
            }}
          >
            <SortableContext
              items={(items ?? []).map((i) => String(i.id))}
              strategy={verticalListSortingStrategy}
            >
              {list}
            </SortableContext>
          </DndContext>
        )}
      </div>
    </div>
  );
};

export default DirectContactManage;
